import React from 'react';
import { BarChart2Icon, TrendingUpIcon, TrendingDownIcon, AlertCircleIcon, CheckCircleIcon, MinusSquareIcon, DollarSignIcon, FileTextIcon, PlusIcon, ClockIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
export function CreditNoteDashboard({
  creditNotes = [],
  setCurrentView,
  locale = 'en-US'
}) {
  // Calculate summary statistics
  const totalCreditNotes = creditNotes.length;
  const totalAmount = creditNotes.reduce((sum, cn) => sum + cn.amount, 0);
  const appliedAmount = creditNotes.reduce((sum, cn) => sum + (cn.appliedAmount || 0), 0);
  const unappliedAmount = creditNotes.reduce((sum, cn) => sum + (cn.unappliedAmount || 0), 0);
  // Count by status
  const statusCounts = creditNotes.reduce((counts, cn) => {
    counts[cn.status] = (counts[cn.status] || 0) + 1;
    return counts;
  }, {});
  // Recent credit notes (last 5)
  const recentCreditNotes = [...creditNotes].sort((a, b) => new Date(b.creditNoteDate) - new Date(a.creditNoteDate)).slice(0, 5);
  return <div data-id="element-3134">
      <div className="mb-8 flex justify-between items-center" data-id="element-3135">
        <div data-id="element-3136">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-3137">
            Credit Notes Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-3138">
            Overview of credit note status and activity
          </p>
        </div>
        <button onClick={() => setCurrentView('credit-note-entry')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-3139">
          <MinusSquareIcon className="h-4 w-4 mr-1" data-id="element-3140" />
          New Credit Note
        </button>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8" data-id="element-3141">
        {/* Total Credit Notes */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-3142">
          <div className="px-4 py-5 sm:p-6" data-id="element-3143">
            <div className="flex items-center" data-id="element-3144">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3" data-id="element-3145">
                <FileTextIcon className="h-6 w-6 text-white" data-id="element-3146" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-3147">
                <dl data-id="element-3148">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3149">
                    Total Credit Notes
                  </dt>
                  <dd className="flex items-baseline" data-id="element-3150">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-3151">
                      {totalCreditNotes}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-3152">
            <div className="text-sm" data-id="element-3153">
              <a href="#" onClick={() => setCurrentView('credit-note-list')} className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-3154">
                View all credit notes
              </a>
            </div>
          </div>
        </div>
        {/* Total Amount */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-3155">
          <div className="px-4 py-5 sm:p-6" data-id="element-3156">
            <div className="flex items-center" data-id="element-3157">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-3158">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-3159" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-3160">
                <dl data-id="element-3161">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3162">
                    Total Credit Amount
                  </dt>
                  <dd className="flex items-baseline" data-id="element-3163">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-3164">
                      {formatCurrency(totalAmount, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-3165">
            <div className="text-sm" data-id="element-3166">
              <span className="font-medium text-gray-500" data-id="element-3167">
                Applied: {formatCurrency(appliedAmount, 'USD', locale)}
              </span>
            </div>
          </div>
        </div>
        {/* Unapplied Amount */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-3168">
          <div className="px-4 py-5 sm:p-6" data-id="element-3169">
            <div className="flex items-center" data-id="element-3170">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3" data-id="element-3171">
                <AlertCircleIcon className="h-6 w-6 text-white" data-id="element-3172" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-3173">
                <dl data-id="element-3174">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3175">
                    Unapplied Amount
                  </dt>
                  <dd className="flex items-baseline" data-id="element-3176">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-3177">
                      {formatCurrency(unappliedAmount, 'USD', locale)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-3178">
            <div className="text-sm" data-id="element-3179">
              <a href="#" className="font-medium text-yellow-600 hover:text-yellow-500" data-id="element-3180">
                {unappliedAmount > 0 ? 'Needs attention' : 'All credits applied'}
              </a>
            </div>
          </div>
        </div>
        {/* Pending Approval */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-3181">
          <div className="px-4 py-5 sm:p-6" data-id="element-3182">
            <div className="flex items-center" data-id="element-3183">
              <div className="flex-shrink-0 bg-blue-500 rounded-md p-3" data-id="element-3184">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-3185" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-3186">
                <dl data-id="element-3187">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-3188">
                    Pending Approval
                  </dt>
                  <dd className="flex items-baseline" data-id="element-3189">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-3190">
                      {statusCounts['Pending'] || 0}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-3191">
            <div className="text-sm" data-id="element-3192">
              <a href="#" className="font-medium text-blue-600 hover:text-blue-500" data-id="element-3193">
                {statusCounts['Pending'] ? 'Review pending credit notes' : 'No pending approvals'}
              </a>
            </div>
          </div>
        </div>
      </div>
      {/* Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-3194">
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-3195">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-3196">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3197">
              Credit Notes by Status
            </h3>
          </div>
          <div className="p-6" data-id="element-3198">
            <div className="space-y-4" data-id="element-3199">
              <div className="flex items-center justify-between" data-id="element-3200">
                <div className="flex items-center" data-id="element-3201">
                  <div className="w-3 h-3 rounded-full bg-gray-300 mr-2" data-id="element-3202"></div>
                  <span className="text-sm text-gray-500" data-id="element-3203">Draft</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3204">
                  {statusCounts['Draft'] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-3205">
                <div className="flex items-center" data-id="element-3206">
                  <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2" data-id="element-3207"></div>
                  <span className="text-sm text-gray-500" data-id="element-3208">Pending</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3209">
                  {statusCounts['Pending'] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-3210">
                <div className="flex items-center" data-id="element-3211">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-3212"></div>
                  <span className="text-sm text-gray-500" data-id="element-3213">Approved</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3214">
                  {statusCounts['Approved'] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-3215">
                <div className="flex items-center" data-id="element-3216">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-2" data-id="element-3217"></div>
                  <span className="text-sm text-gray-500" data-id="element-3218">Posted</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3219">
                  {statusCounts['Posted'] || 0}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-3220">
                <div className="flex items-center" data-id="element-3221">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-2" data-id="element-3222"></div>
                  <span className="text-sm text-gray-500" data-id="element-3223">Rejected</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-3224">
                  {statusCounts['Rejected'] || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-3225">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-3226">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3227">
              Application Status
            </h3>
          </div>
          <div className="p-6" data-id="element-3228">
            <div className="flex items-center justify-between mb-2" data-id="element-3229">
              <span className="text-sm font-medium text-gray-500" data-id="element-3230">
                Total Credit Amount
              </span>
              <span className="text-sm font-medium text-gray-900" data-id="element-3231">
                {formatCurrency(totalAmount, 'USD', locale)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-3232">
              <div className="bg-green-600 h-2.5 rounded-full" style={{
              width: `${appliedAmount / totalAmount * 100}%`
            }} data-id="element-3233"></div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-3234">
              <div className="bg-green-50 p-4 rounded-md" data-id="element-3235">
                <div className="flex items-center" data-id="element-3236">
                  <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-3237" />
                  <span className="text-sm font-medium text-gray-900" data-id="element-3238">
                    Applied
                  </span>
                </div>
                <p className="mt-2 text-lg font-semibold text-gray-900" data-id="element-3239">
                  {formatCurrency(appliedAmount, 'USD', locale)}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3240">
                  {Math.round(appliedAmount / totalAmount * 100)}% of total
                </p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-md" data-id="element-3241">
                <div className="flex items-center" data-id="element-3242">
                  <AlertCircleIcon className="h-5 w-5 text-yellow-500 mr-2" data-id="element-3243" />
                  <span className="text-sm font-medium text-gray-900" data-id="element-3244">
                    Unapplied
                  </span>
                </div>
                <p className="mt-2 text-lg font-semibold text-gray-900" data-id="element-3245">
                  {formatCurrency(unappliedAmount, 'USD', locale)}
                </p>
                <p className="text-xs text-gray-500" data-id="element-3246">
                  {Math.round(unappliedAmount / totalAmount * 100)}% of total
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Recent Credit Notes */}
      <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-3247">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-3248">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-3249">
            Recent Credit Notes
          </h3>
          <a href="#" onClick={() => setCurrentView('credit-note-list')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-3250">
            View all
          </a>
        </div>
        <div className="overflow-x-auto" data-id="element-3251">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-3252">
            <thead className="bg-gray-50" data-id="element-3253">
              <tr data-id="element-3254">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3255">
                  Credit Note #
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3256">
                  Vendor
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3257">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3258">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3259">
                  Status
                </th>
                <th scope="col" className="relative px-6 py-3" data-id="element-3260">
                  <span className="sr-only" data-id="element-3261">View</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-3262">
              {recentCreditNotes.length > 0 ? recentCreditNotes.map(creditNote => <tr key={creditNote.id} className="hover:bg-gray-50" data-id="element-3263">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-3264">
                      {creditNote.creditNoteNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3265">
                      {creditNote.vendor}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3266">
                      {new Date(creditNote.creditNoteDate).toLocaleDateString(locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3267">
                      {formatCurrency(creditNote.amount, creditNote.currency, locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3268">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${creditNote.status === 'Approved' ? 'bg-green-100 text-green-800' : creditNote.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : creditNote.status === 'Draft' ? 'bg-gray-100 text-gray-800' : creditNote.status === 'Posted' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`} data-id="element-3269">
                        {creditNote.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3270">
                      <a href="#" onClick={() => {
                  setCurrentView('credit-note-details');
                }} className="text-indigo-600 hover:text-indigo-900" data-id="element-3271">
                        View
                      </a>
                    </td>
                  </tr>) : <tr data-id="element-3272">
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-3273">
                    No credit notes found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}